import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/system';
import { MenuItem } from '@mui/material';
import { useTheme } from 'styled-components';
import { TAX_RATES } from '../../../../../utils/compound-interest/constants';
import { Tooltip } from '../../../../app/tooltip/tooltip';
import { Select } from '../../../../app/form/select/select';
import { cn } from '../../../../../utils/tailwind';
import { useCompoundInterestCalculatorContext } from '../../calculator-context/compound-interest-calculator-context';

export const CompoundInterestTaxRateSwitcher: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width: 599px)');
    const { params, isTaxExemptionByTestEnabled, handleTaxRateChange } = useCompoundInterestCalculatorContext();

    const parseTaxRateValue = (event: React.ChangeEvent<{ value: unknown }>) => {
        return Number(event.target.value) as 0 | 15 | 23 | 35;
    };

    const frequencyOptionClass = (isActive: boolean) =>
        cn(
            'cursor-pointer rounded-[99rem] border-none px-[1.2rem] text-small font-bold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-45',
            isActive
                ? 'bg-brand-primary text-text-inverse'
                : 'bg-transparent text-neutral-gray hover:bg-[var(--toggle-hover-bg)]',
        );

    return (
        <div className="flex min-h-[4rem] w-full flex-nowrap items-center justify-between gap-[1rem] vl:justify-start">
            <span className="min-w-0 text-small font-medium text-text-muted xl:whitespace-nowrap">
                <Tooltip icon content={t('pages.compoundInterest.calculator.taxRateTooltip')} position="bottom-right">
                    {t('pages.compoundInterest.calculator.taxRate')}
                </Tooltip>
            </span>
            {isMobile ? (
                <Select
                    value={String(params.taxRate)}
                    size={'small'}
                    onChange={(event) =>
                        handleTaxRateChange(
                            parseTaxRateValue(event as unknown as React.ChangeEvent<{ value: unknown }>),
                        )
                    }
                    disabled={isTaxExemptionByTestEnabled}
                    fullWidth={false}
                    minWidth={9}
                >
                    {TAX_RATES.map((rate) => (
                        <MenuItem key={rate} value={String(rate)}>
                            {rate}%
                        </MenuItem>
                    ))}
                </Select>
            ) : (
                <div
                    className="flex h-[4rem] gap-[0.3rem] rounded-[99rem] p-[0.3rem]"
                    style={{
                        background: theme.select.base.open.background,
                        ['--toggle-hover-bg' as string]: `${theme.palette.common.border}30`,
                    }}
                >
                    {TAX_RATES.map((rate) => (
                        <button
                            key={rate}
                            type="button"
                            className={frequencyOptionClass(params.taxRate === rate)}
                            disabled={isTaxExemptionByTestEnabled && rate !== 0}
                            onClick={() => handleTaxRateChange(rate)}
                        >
                            {rate}%
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
